/**
 * PATH: apps/quote/src/components/builder/sheets/AssumptionsSheet.tsx
 * PURPOSE: Display and edit quote assumptions (named ranges / config values)
 *
 * WHY:
 * - Excel quotes have named ranges for key assumptions (labour rate, markups, etc.)
 * - Users need to see and adjust these values in a structured way
 * - Changes should be audited and trigger recalculation
 *
 * FLOW:
 *   Load config → Group by category → Edit inline → Save → Recalculate
 *
 * SIZE NOTE (over 300-line cap): File currently houses three tightly
 *   coupled components — AssumptionRow, CategorySection, and the top-level
 *   AssumptionsSheet container — plus the ASSUMPTION_CATEGORIES schema and
 *   CustomColumnsSection/VisibleColumnsSection consumers. They share the
 *   `config` shape, the `isEditable` prop, and the categorisation taxonomy.
 *   A clean split (e.g. extract AssumptionRow + CategorySection into
 *   sibling files) is a deliberate refactor on its own MR; doing it here
 *   would dilute the org-default-labour-rate change and pull more files
 *   into review than necessary (rule 1.4: complete the job, but not by
 *   bundling unrelated edits — rule "keep changes minimal and reversible").
 *   Tracked as follow-up: split AssumptionsSheet by responsibility.
 */

import { useMemo, useState, useCallback } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Save,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Info,
  Clock,
  AlertCircle,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { quoteApi, type QuoteDetail } from '@/lib/quoteApi'
import { useOrganisationDefaults } from '@/hooks/useOrganisationDefaults'
import CustomColumnsSection from './CustomColumnsSection'
import VisibleColumnsSection from './VisibleColumnsSection'
import ExcelAssumptionsContent from './ExcelAssumptionsContent'

// Maps a category-item `key` (e.g. 'labourCostRate') to a tenant
// default surfaced via OrganisationSerializer. Keys not present here
// have no org-level default — the AssumptionRow falls back to its
// usual behaviour (no "use org default" button). Currently only the
// labour cost rate is wired; future fields (e.g. material markup
// org defaults) extend this map without touching AssumptionRow.
type OrgDefaultsMap = Readonly<Record<string, number | null | undefined>>

interface AssumptionsSheetProps {
  quoteId: string
  quote: QuoteDetail
  isEditable: boolean
}

/**
 * Assumption categories using BACKEND config keys (camelCase).
 * 
 * IMPORTANT: Backend stores percentages as decimals (0.10 = 10%).
 * The UI displays them as percentages (10%) and converts on save.
 * 
 * Keys match: repos/quotech-backend/quotebuilder/models/quote.py config structure
 */
interface AssumptionItem {
  key: string
  label: string
  unit: string
  description: string
  isPercent?: boolean  // If true, display value*100, save value/100
}

interface AssumptionCategory {
  id: string
  title: string
  description: string
  color: string
  items: AssumptionItem[]
}

const ASSUMPTION_CATEGORIES: AssumptionCategory[] = [
  {
    id: 'labour',
    title: 'Labour Rates',
    description: 'Labour cost and chargeout rates',
    color: 'blue',
    items: [
      { key: 'labourCostRate', label: 'Labour Cost Rate', unit: '$/hr', description: 'Base hourly cost for labour' },
      { key: 'labourChargeOutRate', label: 'Labour Chargeout Rate', unit: '$/hr', description: 'Billable hourly rate' },
      { key: 'hoursPerDay', label: 'Hours Per Day', unit: 'hrs', description: 'Working hours in a standard day' },
    ],
  },
  {
    id: 'productivity',
    title: 'Productivity',
    description: 'Productivity factors and buffers',
    color: 'emerald',
    items: [
      { key: 'defaultProductivityPmpd', label: 'Default Productivity (PMPD)', unit: 'units/day', description: 'Default production-per-man-day used to seed new line items' },
      { key: 'downtimeFactor', label: 'Downtime Factor', unit: '%', description: 'Buffer for non-productive time', isPercent: true },
    ],
  },
  {
    id: 'materials',
    title: 'Material Markups',
    description: 'Markup percentages for material costs',
    color: 'amber',
    items: [
      { key: 'materialMarkup', label: 'Material Markup', unit: '%', description: 'Standard markup on material cost', isPercent: true },
      { key: 'materialWastage', label: 'Material Wastage', unit: '%', description: 'Default wastage allowance', isPercent: true },
    ],
  },
  {
    id: 'plant',
    title: 'Plant & Equipment',
    description: 'Rates and markups for plant equipment',
    color: 'purple',
    items: [
      { key: 'plantMarkup', label: 'Plant Markup', unit: '%', description: 'Markup on plant hire costs', isPercent: true },
    ],
  },
  {
    id: 'subcon',
    title: 'Subcontractors',
    description: 'Subcontractor cost markups',
    color: 'teal',
    items: [
      { key: 'subcontractorMarkup', label: 'Subcontractor Markup', unit: '%', description: 'Margin added to subcon costs', isPercent: true },
    ],
  },
  {
    id: 'other',
    title: 'General / GST',
    description: 'GST and general assumptions',
    color: 'slate',
    items: [
      { key: 'gstRate', label: 'GST Rate', unit: '%', description: 'Goods and Services Tax rate', isPercent: true },
    ],
  },
]

function AssumptionRow({
  itemKey,
  label,
  unit,
  description,
  value,
  onChange,
  isEditable,
  isPercent = false,
  orgDefault = null,
}: {
  itemKey: string
  label: string
  unit: string
  description: string
  value: number | string | null | undefined
  onChange: (newValue: number) => void
  isEditable: boolean
  isPercent?: boolean
  /**
   * Tenant-level baseline for this assumption (e.g. the org's
   * default_labour_rate $/hr). When provided AND differs from
   * the current per-quote value AND row is editable, render an
   * "Apply org default" mini-button. Null disables the feature.
   */
  orgDefault?: number | null
}) {
  const [editing, setEditing] = useState(false)
  
  // For percent fields: backend stores 0.10, we display 10
  const displayNumber = useMemo(() => {
    if (value === null || value === undefined) return null
    const num = Number(value)
    return isPercent ? num * 100 : num
  }, [value, isPercent])
  
  const [editValue, setEditValue] = useState(String(displayNumber ?? ''))

  // Update editValue when value changes externally
  useMemo(() => {
    if (!editing) {
      setEditValue(String(displayNumber ?? ''))
    }
  }, [displayNumber, editing])

  const displayValue = useMemo(() => {
    if (displayNumber === null) return '—'
    if (isPercent || unit === '%') return `${displayNumber.toFixed(1)}%`
    if (unit === '$/hr' || unit === '$/day') return `$${displayNumber.toFixed(2)}`
    return String(displayNumber)
  }, [displayNumber, unit, isPercent])

  // "Apply org default" pill is only rendered when:
  //   - orgDefault is a usable number (parseDefaultLabourRate filters
  //     out zero / negative / NaN upstream)
  //   - row is editable (read-only quotes / locked sections don't
  //     show a button that would 403 on click)
  //   - the org default actually differs from the current value
  //     (otherwise the pill is noise — value already matches)
  //
  // Note: only wired for non-percent units. Percent fields would need
  // additional decimal⇄% conversion semantics on apply; we don't have
  // a use case for org-default markup percentages yet, so leaving
  // that surface unbuilt avoids confusing UX (rule 1.4: ship complete,
  // not half-built).
  const showOrgDefaultPill =
    orgDefault !== null &&
    orgDefault !== undefined &&
    Number.isFinite(orgDefault) &&
    !isPercent &&
    isEditable &&
    !editing &&
    Number(value) !== orgDefault

  const orgDefaultLabel = useMemo(() => {
    if (orgDefault === null || orgDefault === undefined) return null
    if (unit === '$/hr' || unit === '$/day') return `$${orgDefault.toFixed(2)}`
    return String(orgDefault)
  }, [orgDefault, unit])

  const handleCommit = () => {
    setEditing(false)
    const parsed = parseFloat(editValue)
    if (!isNaN(parsed)) {
      // For percent fields: UI shows 10, we save 0.10
      const saveValue = isPercent ? parsed / 100 : parsed
      if (saveValue !== Number(value)) {
        onChange(saveValue)
      }
    }
  }

  const handleApplyOrgDefault = () => {
    if (orgDefault === null || orgDefault === undefined) return
    if (!Number.isFinite(orgDefault)) return
    // Skip onChange call when value already matches — guards against
    // a stray double-click triggering a pointless recalculate.
    if (Number(value) === orgDefault) return
    onChange(orgDefault)
  }

  const handleStartEditing = () => {
    if (!isEditable) return
    setEditValue(String(displayNumber ?? ''))
    setEditing(true)
  }

  return (
    <div className="grid grid-cols-[200px_1fr_120px_120px] items-center gap-4 px-4 py-2 hover:bg-slate-50 dark:hover:bg-slate-800/50 rounded">
      <div>
        <div className="font-medium text-sm text-slate-900 dark:text-white">{label}</div>
        <div className="text-xs text-slate-500 dark:text-slate-400">{description}</div>
      </div>
      <div className="text-xs text-slate-400 font-mono">{itemKey}</div>
      <div className="text-center">
        {editing ? (
          <input
            type="number"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleCommit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCommit()
              if (e.key === 'Escape') {
                setEditValue(String(displayNumber ?? ''))
                setEditing(false)
              }
            }}
            className="w-full px-2 py-1 text-sm font-mono text-right border-2 border-blue-500 rounded bg-white dark:bg-slate-800 outline-none"
            step={isPercent ? '0.1' : '0.01'}
            autoFocus
          />
        ) : (
          <div
            className={cn(
              'px-2 py-1 text-sm font-mono text-right rounded',
              isEditable && 'cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20'
            )}
            onClick={handleStartEditing}
          >
            {displayValue}
          </div>
        )}
        {showOrgDefaultPill && orgDefaultLabel && (
          <button
            type="button"
            onClick={handleApplyOrgDefault}
            className={cn(
              'mt-1 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded',
              'border border-emerald-200 dark:border-emerald-800',
              'text-emerald-700 dark:text-emerald-300',
              'bg-emerald-50 dark:bg-emerald-900/30',
              'hover:bg-emerald-100 dark:hover:bg-emerald-900/50',
              'transition-colors',
            )}
            title="Apply your organisation's default value to this quote"
            data-testid={`apply-org-default-${itemKey}`}
          >
            <span aria-hidden="true">↩</span>
            <span>Org default {orgDefaultLabel}</span>
          </button>
        )}
      </div>
      <div className="text-xs text-slate-400 text-right">{unit}</div>
    </div>
  )
}

function CategorySection({
  category,
  config,
  isEditable,
  onUpdate,
  defaultExpanded = true,
  orgDefaults,
}: {
  category: AssumptionCategory
  config: Record<string, any>
  isEditable: boolean
  onUpdate: (key: string, value: number) => void
  defaultExpanded?: boolean
  // Optional map of item.key → org default. Forwarded into each
  // AssumptionRow; rows with no entry behave exactly as before.
  orgDefaults?: OrgDefaultsMap
}) {
  const [expanded, setExpanded] = useState(defaultExpanded)

  const colorClasses: Record<string, string> = {
    blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-800',
    amber: 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-800',
    purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 border-purple-200 dark:border-purple-800',
    teal: 'bg-teal-100 dark:bg-teal-900/30 text-teal-800 dark:text-teal-300 border-teal-200 dark:border-teal-800',
    slate: 'bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-300 border-slate-200 dark:border-slate-700',
    emerald: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800',
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className={cn('w-full flex items-center justify-between px-4 py-3', colorClasses[category.color])}
      >
        <div className="flex items-center gap-2">
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <span className="font-semibold">{category.title}</span>
          <span className="text-xs opacity-70">• {category.description}</span>
        </div>
        <span className="text-xs opacity-50">{category.items.length} items</span>
      </button>

      {expanded && (
        <div className="bg-white dark:bg-slate-900 divide-y divide-slate-100 dark:divide-slate-800">
          {/* Header */}
          <div className="grid grid-cols-[200px_1fr_120px_120px] gap-4 px-4 py-2 bg-slate-50 dark:bg-slate-800/50 text-xs font-medium text-slate-500 dark:text-slate-400">
            <div>Setting</div>
            <div>Key</div>
            <div className="text-center">Value</div>
            <div className="text-right">Unit</div>
          </div>

          {/* Rows */}
          {category.items.map((item) => (
            <AssumptionRow
              key={item.key}
              itemKey={item.key}
              label={item.label}
              unit={item.unit}
              description={item.description}
              value={config[item.key]}
              onChange={(value) => onUpdate(item.key, value)}
              isEditable={isEditable}
              isPercent={item.isPercent}
              orgDefault={orgDefaults?.[item.key] ?? null}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default function AssumptionsSheet({
  quoteId,
  quote,
  isEditable,
}: AssumptionsSheetProps) {
  const queryClient = useQueryClient()
  const config = quote.config || {}
  const [lastRecalculated, setLastRecalculated] = useState<Date | null>(null)

  // Resolve the org's tenant-level defaults so the labour cost rate
  // row can render an "Apply org default" button. Hook returns null
  // while the network is in flight; the AssumptionRow treats that as
  // "no default — render normally". Shares the TanStack Query cache
  // with useQuoteBuilderTradeMode, so this is free network-wise.
  const { defaultLabourRate } = useOrganisationDefaults()
  const orgDefaults: OrgDefaultsMap = useMemo(
    () => ({ labourCostRate: defaultLabourRate }),
    [defaultLabourRate],
  )

  // Mutation to update quote config + trigger recalculation
  const updateConfigMutation = useMutation({
    mutationFn: async (updates: Record<string, number>) => {
      // 1. Update the quote config
      await quoteApi.updateQuote(quoteId, { config: { ...config, ...updates } })
      
      // 2. Trigger full recalculation of all line items
      // This applies the new config values to all line items
      return quoteApi.recalculateQuote(quoteId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quote', 'builder', quoteId] })
      setLastRecalculated(new Date())
    },
  })

  const handleUpdateAssumption = useCallback((key: string, value: number) => {
    updateConfigMutation.mutate({ [key]: value })
  }, [updateConfigMutation])

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">Quote Assumptions</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Configure rates, markups, and calculation parameters
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
          <Info className="w-4 h-4" />
          <span>Changes trigger automatic recalculation</span>
        </div>
      </div>

      {/* Info banner */}
      <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-medium text-blue-900 dark:text-blue-200">
            These values mirror Excel named ranges
          </p>
          <p className="text-blue-700 dark:text-blue-300 mt-1">
            When you import an Excel quote, these values are extracted from named ranges.
            Modifying them here will update all line item calculations.
          </p>
        </div>
      </div>

      {/* Categories */}
      <div className="space-y-4">
        {ASSUMPTION_CATEGORIES.map((category, idx) => (
          <CategorySection
            key={category.id}
            category={category}
            config={config}
            isEditable={isEditable}
            onUpdate={handleUpdateAssumption}
            defaultExpanded={idx === 0}
            orgDefaults={orgDefaults}
          />
        ))}
        <CustomColumnsSection
          quoteId={quoteId}
          quote={quote}
          isEditable={isEditable}
          defaultExpanded={false}
        />
        <VisibleColumnsSection
          quoteId={quoteId}
          quote={quote}
          isEditable={isEditable}
          defaultExpanded={false}
        />
      </div>

      {/* Excel "Assumptions" sheet content — per-line comments,
          Works Program, Grout Compartments. Renders nothing when
          Quote.config.assumptions is absent (variations etc). */}
      <ExcelAssumptionsContent quote={quote} />

      {/* Save/Recalc status indicator */}
      {updateConfigMutation.isPending && (
        <div className="fixed bottom-20 right-4 flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg shadow-lg">
          <RefreshCw className="w-4 h-4 animate-spin" />
          <span className="text-sm">Saving & recalculating...</span>
        </div>
      )}

      {updateConfigMutation.isSuccess && lastRecalculated && (
        <div className="fixed bottom-20 right-4 flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg shadow-lg animate-fade-out">
          <Save className="w-4 h-4" />
          <span className="text-sm">Saved! All items recalculated.</span>
        </div>
      )}
    </div>
  )
}

